// Re-exported rather than redefined: the sheet and the tab counting its rows have to
// agree on which level is the leaf, and they sit in different layers.
export { leafLevelOf } from '@/entities/media-campaign';
import {
  fieldDef,
  levelSchema,
  nodeIssues,
  leafLevelOf,
  nodeName,
  platformStructure,
  type JobIssue,
  type JobNode,
  type JobPlatformGroup,
  type PlatformFieldDef,
  type PlatformLevel,
} from '@/entities/media-campaign';

/** A column of the sheet. Only ever the selected level's own fields. */
export interface JobTableColumn {
  key: string;
  fieldKey: string;
  def: PlatformFieldDef;
  width: number;
}

/** One leaf object, plus the parents that name it. */
export interface LeafRow {
  id: string;
  node: JobNode;
  ancestors: Partial<Record<PlatformLevel, JobNode>>;
}

/**
 * A row of the grouped view: a band for a parent, or a leaf beneath it.
 *
 * The flat view is the default because the source spreadsheet is flat, but a
 * plan with many campaigns reads better folded, so grouping is offered as a
 * mode rather than replacing it.
 */
export type SheetRow =
  | { kind: 'band'; id: string; node: JobNode; level: PlatformLevel; depth: number; leaves: number }
  | { kind: 'leaf'; id: string; node: JobNode };

const DEFAULT_COLUMN_WIDTH = 170;
const WIDE_COLUMN_WIDTH = 220;
const WIDE_KINDS: ReadonlyArray<PlatformFieldDef['kind']> = ['tokens'];

/**
 * The selected level's own fields, minus `name` — that is the sticky first
 * column, and repeating it inside the scroll region would waste the widest cell
 * on screen.
 *
 * One level at a time is the whole point of the view: a Meta ad row carries 26
 * fields across the three tiers, and nobody reviews all three at once. They
 * sweep a level ("are the ad set budgets right"), which is also how every
 * platform's own UI is laid out.
 */
export function levelFieldColumns(platform: string, level: PlatformLevel): JobTableColumn[] {
  return levelSchema(platform, level)
    .fields.filter((def) => def.key !== 'name')
    .map((def) => ({
      key: `${level}.${def.key}`,
      fieldKey: def.key,
      def,
      width: WIDE_KINDS.includes(def.kind) ? WIDE_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH,
    }));
}

/** Levels above the leaf, outermost first — the parent name columns. */
export function ancestorLevelsOf(platform: string): PlatformLevel[] {
  const order = platformStructure(platform).order;
  return order.slice(0, -1);
}

/**
 * One row per leaf object, in file order, each carrying the parents that name it.
 *
 * Flat rather than grouped: the source spreadsheet is flat, so a row here lines
 * up with a row there. The parents are name-only columns — their configuration
 * lives in the details drawer, which is what keeps this sheet narrow enough to
 * read.
 */
export function leafRows(group: JobPlatformGroup, platform: string): LeafRow[] {
  const leafLevel = leafLevelOf(platform);
  const rows: LeafRow[] = [];

  const walk = (node: JobNode, ancestors: Partial<Record<PlatformLevel, JobNode>>) => {
    if (node.level === leafLevel) {
      rows.push({ id: node.id, node, ancestors });
      return;
    }
    const next = { ...ancestors, [node.level]: node };
    for (const child of node.children) walk(child, next);
  };

  for (const node of group.nodes) walk(node, {});

  return rows;
}

/**
 * Which rows a parent's name cell should be merged across, spreadsheet style.
 *
 * A flat row is one ad, so it repeats its campaign and its ad set on every line.
 * Twelve ads under one campaign print that campaign's name twelve times — and,
 * once pre-flight has run, its findings twelve times too, which reads as twelve
 * problems where there is one. Saying it once, spanning its rows, is both less
 * noise and a truer count.
 *
 * Keyed by row id: a present number is the `rowSpan` for that cell, and an absent
 * one means the row is covered by a cell above and renders nothing at all.
 *
 * Runs are contiguous by construction, because `leafRows` walks the tree
 * depth-first and a parent's leaves are therefore neighbours.
 */
export function ancestorSpans(
  rows: LeafRow[],
  levels: PlatformLevel[],
): Map<string, Partial<Record<PlatformLevel, number>>> {
  const spans = new Map<string, Partial<Record<PlatformLevel, number>>>();

  for (const level of levels) {
    let start = 0;
    while (start < rows.length) {
      const id = rows[start].ancestors[level]?.id ?? null;
      let end = start + 1;
      while (end < rows.length && (rows[end].ancestors[level]?.id ?? null) === id) end += 1;

      const at = spans.get(rows[start].id) ?? {};
      at[level] = end - start;
      spans.set(rows[start].id, at);
      start = end;
    }
  }

  return spans;
}

function countLeaves(node: JobNode, leafLevel: PlatformLevel): number {
  if (node.level === leafLevel) return 1;
  return node.children.reduce((sum, child) => sum + countLeaves(child, leafLevel), 0);
}

/**
 * The same plan as bands and leaves, for the grouped mode.
 *
 * Collapsing drops a band's descendants from the list rather than hiding them
 * with CSS, so folding a plan with hundreds of line items costs nothing.
 */
export function buildSheetRows(
  group: JobPlatformGroup,
  platform: string,
  collapsed: ReadonlySet<string>,
): SheetRow[] {
  const leafLevel = leafLevelOf(platform);
  const rows: SheetRow[] = [];

  const walk = (node: JobNode, depth: number) => {
    if (node.level === leafLevel) {
      rows.push({ kind: 'leaf', id: node.id, node });
      return;
    }

    rows.push({
      kind: 'band',
      id: node.id,
      node,
      level: node.level,
      depth,
      leaves: countLeaves(node, leafLevel),
    });

    if (collapsed.has(node.id)) return;
    for (const child of node.children) walk(child, depth + 1);
  };

  for (const node of group.nodes) walk(node, 0);

  return rows;
}

/** Every ancestor id, so the grouped view can unfold the path to a hidden cell. */
export function ancestorIdsOf(group: JobPlatformGroup, nodeId: string): string[] {
  const walk = (node: JobNode, trail: string[]): string[] | null => {
    if (node.id === nodeId) return trail;
    for (const child of node.children) {
      const hit = walk(child, [...trail, node.id]);
      if (hit) return hit;
    }
    return null;
  };

  for (const node of group.nodes) {
    const hit = walk(node, []);
    if (hit) return hit;
  }
  return [];
}

export function countAtLevel(group: JobPlatformGroup, level: PlatformLevel): number {
  const walk = (nodes: JobNode[]): number =>
    nodes.reduce((sum, node) => sum + (node.level === level ? 1 : 0) + walk(node.children), 0);
  return walk(group.nodes);
}

/** One problem, tied to the exact cell — and level — that can fix it. */
export interface RowIssue {
  nodeId: string;
  level: PlatformLevel;
  nodeName: string;
  /** Platform's own label for the field, or null for a node-level issue. */
  fieldLabel: string | null;
  issue: JobIssue;
}

function issuesOf(platform: string, node: JobNode): RowIssue[] {
  return nodeIssues(platform, node).map((issue) => ({
    nodeId: node.id,
    level: node.level,
    nodeName: nodeName(node),
    fieldLabel:
      issue.field === null
        ? null
        : (fieldDef(platform, node.level, issue.field)?.label ?? issue.field),
    issue,
  }));
}

export function nodeRowIssues(platform: string, node: JobNode): RowIssue[] {
  return issuesOf(platform, node);
}

/**
 * Every issue on the platform, at every level.
 *
 * The drawer spans levels on purpose: a missing campaign objective has to be
 * reachable while you are looking at the Ads view, or switching levels becomes
 * a way to lose problems.
 */
export function allIssues(platform: string, group: JobPlatformGroup): RowIssue[] {
  const walk = (nodes: JobNode[]): RowIssue[] =>
    nodes.flatMap((node) => [...issuesOf(platform, node), ...walk(node.children)]);
  return walk(group.nodes);
}

export function countSeverity(issues: RowIssue[]) {
  return {
    errors: issues.filter((entry) => entry.issue.severity === 'error').length,
    warnings: issues.filter((entry) => entry.issue.severity === 'warning').length,
    advice: issues.filter((entry) => entry.issue.severity === 'advice').length,
  };
}
